/**
 * Geschätzte Zuschauer je Videominute der Erstausstrahlung im eigenen Webinar-Raum (Detailseite).
 * Dieselbe Rechnung wie die Kurve der Montagsmail (`webinarReportData.aggregateRoom`): je Zuschauer zählt
 * die Spanne von Einstieg bis weitester Position — Pausen dazwischen zählen mit, daher „geschätzt".
 * Zweite Linie: Zuschauer ohne Späteinsteiger-Zuteilung (= ab Beginn dabei).
 */
import { FC, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import type { RoomRetentionData } from '../../helpers/webinarStatisticsData';
import { Legende, TipRow, achse, useVizStyle } from './vizStyle';

/**
 * `sektion` (Opt-in, nur Sektions-Detailseite, Persona-Check 26.10.2026 B19/B27): Linie „zum Videostart schon im
 * Raum" statt „ohne Späteinstieg" und je ein Satz, warum die Kurve nicht mit „eingestiegen" und nicht mit „Angebot
 * gesehen" übereinstimmt. Ohne den Schalter (alte Seite) unverändert.
 */
const RoomRetentionChart: FC<{ data: NonNullable<RoomRetentionData['room']>; sektion?: boolean }> = ({ data, sektion = false }) => {
  const viz = useVizStyle();
  const series = useMemo(
    () => [
      { id: 'alle', data: data.retention.map((p) => ({ x: p.minute, y: p.all || 0 })) },
      { id: 'ab Beginn', data: data.retention.map((p) => ({ x: p.minute, y: p.punctual || 0 })) },
    ],
    [data.retention],
  );
  const atOffer =
    data.offerMinute != null
      ? data.retention.find((p) => p.minute === data.offerMinute)
      : undefined;
  const pct = (a: number, b: number) => (b > 0 ? `${Math.round((100 * a) / b)} %` : '–');
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Geschätzte Zuschauer je Videominute
      </Typography>
      <Typography variant="body2" sx={{ color: viz.inkSecondary, mb: 1.5 }}>
        {data.viewers.toLocaleString('de-DE')} Zuschauer im Raum · im Schnitt{' '}
        {Math.round(data.avgWatchedSec / 60)}
        {data.videoMinutes ? ` von ${data.videoMinutes}` : ''} Minuten gesehen
        {atOffer && data.offerMinute != null
          ? ` · beim Angebot in Minute ${data.offerMinute} noch ${atOffer.all.toLocaleString('de-DE')} (${pct(atOffer.all, data.viewers)})`
          : ''}
        . Geschätzt aus Einstieg und weitester Position je Person — wer pausiert und später
        weiterschaut, zählt für die Minuten dazwischen mit.
        {sektion && (
          <>
            {' '}
            Später zugeschaltet heißt hier: erst mehr als 2 Minuten nach Beginn in den Raum gekommen — nicht dasselbe wie
            „während des Webinars eingestiegen“ (erst nach Beginn angemeldet). Die Kurve schätzt, wer genau in einer Minute
            zusah; „Angebot gesehen“ zählt jede Person einmal, die das Angebot irgendwann gesehen oder geklickt hat — auch
            wer danach wieder gegangen ist. Deshalb kann es mehr sein als die Kurve in der Angebotsminute zeigt.
          </>
        )}
      </Typography>
      <Legende
        items={[
          { label: 'alle Zuschauer', color: viz.series1, shape: 'line' },
          { label: sektion ? 'zum Videostart schon im Raum' : 'ohne Späteinstieg (ab Beginn dabei)', color: viz.muted, shape: 'line' },
        ]}
      />
      <Box sx={{ height: 280 }}>
        <ResponsiveLine
          data={series}
          animate={false}
          theme={viz.nivo as any}
          colors={[viz.series1, viz.muted]}
          margin={{ top: 26, right: 24, bottom: 44, left: 52 }}
          xScale={{ type: 'linear', min: 0, max: 'auto' }}
          yScale={{ type: 'linear', min: 0, max: 'auto' }}
          curve="monotoneX"
          lineWidth={2}
          enablePoints={false}
          enableGridX={false}
          useMesh
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            legend: 'Videominute',
            legendOffset: 34,
            legendPosition: 'middle',
            format: achse(),
          }}
          axisLeft={{ tickSize: 0, tickPadding: 8, format: achse() }}
          markers={
            data.offerMinute != null
              ? [
                  {
                    axis: 'x',
                    value: data.offerMinute,
                    lineStyle: { stroke: viz.series2, strokeWidth: 1.5, strokeDasharray: '4 4' },
                    legend: 'Angebot',
                    legendPosition: 'top',
                    textStyle: { fill: viz.inkSecondary, fontSize: 12 },
                  },
                ]
              : []
          }
          tooltip={({ point }) => (
            <Box sx={{ ...(viz.nivo as any).tooltip.container, p: 1 }}>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>Minute {String(point.data.x)}</div>
              <TipRow
                color={point.serieColor}
                label={point.serieId === 'alle' ? 'Zuschauer' : 'ab Beginn dabei'}
                value={Number(point.data.y).toLocaleString('de-DE')}
              />
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

export default RoomRetentionChart;
